import React, { useEffect, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import CssBaseline from '@material-ui/core/CssBaseline';
import * as am4core from '@amcharts/amcharts4/core';
// eslint-disable-next-line camelcase
import am4themes_material from '@amcharts/amcharts4/themes/material';
import { ModeUIStateProvider } from '@moderepo/mode-ui-state';
import { makeStyles, CircularProgress } from '@material-ui/core';
import { LoadingContainer } from '@moderepo/mode-app-components';
import { DEFAULT_ORGANIZATION_ID, OrganizationInfo, RitzAPI } from '@moderepo/mode-internal-apis';
import { AppRoutes } from '..';
import '../../i18n/i18n';
import '../../assets/style/_global.scss';
import { ModeThemeProvider } from '../providers/ModeThemeProvider';
import { TranslationProvider } from '../providers/TranslationProvider';
import { DatePickerUtilsProvider } from '../providers/DatePickerUtilsProvider';
import { NotificationProvider } from '../providers/NotificationContainer';
import { GlobalDataStateProviderContainer } from '../providers/GlobalDataStateProviderContainer';
import { GenericDialogProvider } from '../providers/GenericDialogProvider';


am4core.useTheme(am4themes_material);
if (process.env.REACT_APP_AMCHARTS_LICENSE) {
    am4core.addLicense(process.env.REACT_APP_AMCHARTS_LICENSE);
}


const useStyle = makeStyles(() => {
    return {
        app: {
            width   : '100%',
            height  : '100%',
            overflow: 'hidden',
        },
    };
}, { name: 'App' });



// Routes with organization id needs to have this prefix
export const ROUTE_WITH_ORG_ID_PREFIX = '/orgs/';


/**
 * Sometimes orgId is passed to the URL as query param like this https://ops.tinkermode.com/login?orgId=sbr
 * If we detect orgId in the search params, we will redirect the URL to the same page but with the orgId as part of the pathname instead
 * of search param. So for the above example, the user will be redirect to this URL https://ops.tinkermode.com/orgs/sbr/login
 */
const searchParams = new URLSearchParams(window.location.search);
const orgId = searchParams.get('orgId');
if (orgId && orgId.length > 0) {
    // Delete the orgId from the searchParams
    searchParams.delete('orgId');

    // Recreate a new search param string WITHOUT the "orgId=xxx" param BUT only if there are still some params left in the searchParams
    // If after deleting "orgId=xxx" and there is no more param left then the new searchParams string will just going to be ""
    const paramsCount = Array.from(searchParams.keys()).length;
    const updatedSearchParams = paramsCount > 0 ? `?${searchParams.toString()}` : '';

    // eslint-disable-next-line max-len
    const newURL = `${window.location.origin}${ROUTE_WITH_ORG_ID_PREFIX}${orgId}${window.location.pathname}${updatedSearchParams}${window.location.hash}`;
    window.location.href = newURL;
}



/**
 * Get the route baseName from the location.pathname. If the pathname starts with the ROUTE_WITH_ORG_ID_PREFIX, we will assume the URL contains
 * organization info. If it does, we will use that as the baseName of the app. If the URL does not starts with the ROUTE_WITH_ORG_ID_PREFIX then we
 * will use "" as baseName.
 *
 *      If pathname = "http://ops.tinkermode.com/login" => baseName = ""
 *      If pathname = "http://ops.tinkermode.com/orgs/panasonic/login" => baseName = "/orgs/panasonic"
 *      If pathname = "http://ops.tinkermode.com/orgs/sbr/projects/1234" => baseName = "orgs/sbr"
 *      If pathname = "http://ops.tinkermode.com/orgs/sbr" => baseName = "orgs/sbr"
 */
const getBaseName = () => {
    const pathname = window.location.pathname;
    if (pathname.startsWith(ROUTE_WITH_ORG_ID_PREFIX)) {
        // Make sure the is an / at the end of the /orgs/ORG_ID/. If there isn't another / after /org/ then use the entire pathname
        // as baseName. This can happen when the user go to this link for example "http://ops.tinkermode.com/orgs/sbr"
        const lastIndex = pathname.indexOf('/', ROUTE_WITH_ORG_ID_PREFIX.length);
        const basename = pathname.substring(0, lastIndex > 0 ? lastIndex : pathname.length);
        return basename;
    }
    return '';
};
const ROUTE_BASENAME = getBaseName();


/**
 * Get the orgId from the route's basename
 * @returns
 */
const getOrgIdFromBasename = (basename: string): string | undefined => {
    if (basename && basename.length > 0) {
        // Remove the ROUTE_WITH_ORG_ID_PREFIX from the basename to get the orgId. basename is like this
        // "orgs/panasonic" and ROUTE_WITH_ORG_ID_PREFIX is "/orgs/" so we remove the "/orgs/" and we get the "panasonic" as orgId
        const orgId = basename.substring(ROUTE_WITH_ORG_ID_PREFIX.length);
        if (orgId && orgId.length > 0) {
            return orgId;
        }
    }

    return undefined;
};



// Create a reference to the current organization but don't export it because it is not a constant. If we export it, someone
// can modify from other part of the code
let organization: OrganizationInfo | undefined;

// Export a function to return the organization.
export const getOrganization = (): OrganizationInfo | undefined => {
    return organization;
};



/**
 * Fetch organization data based on the URL and return the organization object/s or undefined if the URL is invalid.
 * There are 2 info in the URL that determine what organization we need to use, the baseUrl and the orgId in the URL path. There will always
 * going to be an organization based on baseUrl but organization based on orgId is optional. This function will return at least 1 organization
 * object, orgByBaseUrl, and if there is orgId in the URL, it will also return orgByOrgId.
 *
 * Organization based on baseUrl examples:
 *      https://ops.tinkermode.dev          // OPS console default URL for most projects that don't have custom hostname. This will be "default" organization
 *      https://yco-ops.tinkermode.dev      // OPS console URL for "y_company" which has custom hostname. This will be "y_company" organization.
 *      https://panasonic.agv.com           // OPS console URL for "panasonic_agv" which has custom hostname. This will be "panasonic_agv" organization
 *
 * Organization based on orgId in URL examples:
 *      https://ops.tinkermode.dev/orgs/x_company       // orgId = "x_company"
 *      https://ops.tinkermode.dev/orgs/default         // orgId = "default". This is redundant because organization based on host name is also for "default" organization.
 *      https://yco-ops.tinkermode.dev/orgs/y_company   // orgId = "y_company". This can happen but redundant because the hostname and orgId is for the same organization
 *      https://yco-ops.tinkermode.dev/orgs/x_company   // orgId = "x_company". This can happen but invalid because the hostname and orgId is for different organization
 *      https://yco-ops.tinkermode.dev/orgs/default     // orgId = "default". This can happen but invalid because the hostname and orgId is for different organization
 *
 * So first, we will fetch organization based on hostname. Then we check if the url contains orgId info.
 *  - If there is no orgId info in the URL then we are good, we will use the organization based on the hostname
 *  - If the URL contains orgId then we will see if we need to fetch organization for the orgId
 *      - Case 1: https://ops.tinkermode.dev/orgs/x_company
 *          For this case, we WILL need to load organization for "x_company" because "https://ops.tinkermode.dev" is the OP Console
 *          default URL. Therefore, the "orgByBaseUrl" will be an object for the "default" company, NOT "x_company". Therefore, we will
 *          need to load organization data for "x_company" and that will be the organization that we will be using.
 *
 *      - Case 2: https://yco-ops.tinkermode.dev/orgs/y_company
 *          For this case, we WILL NOT load organization for "y_company" because we already have the data. "https://yco-ops.tinkermode.dev" is the
 *          OP Console URL for "y_company". Therefore, the "orgByBaseUrl" should be the object for "y_company" already so we don't need
 *          to load data for "y_company" anymore.
 *
 *      - Case 3: https://yco-ops.tinkermode.dev/orgs/x_company or https://yco-ops.tinkermode.dev/orgs/default
 *          Because "https://yco-ops.tinkermode.dev" is for the OP Console URL for "y_company". Therefore, the "orgByBaseUrl" should
 *          be the object for "y_company". The orgId is for "x_company" or "default" company which is not the same as the "orgByBaseUrl".
 *          This is INVALID URL and should result in error message.
 */
const loadOrganization = async (): Promise<{
    readonly orgByBaseUrl: OrganizationInfo;
    readonly orgByOrgId?: OrganizationInfo | undefined;
} | undefined> => {
    try {
        // First, get the 'organization' object based on the baseUrl e.g. https://ops.tinkermode.dev or https://yco-ops.tinkermode.dev, etc...
        const baseUrl = window.location.origin;
        const orgByBaseUrl = (await RitzAPI.getInstance().getOrganizationByBaseUrl(baseUrl))[0];

        // Check if the URL contains orgId e.g. /orgs/default, /orgs/x_company, /orgs/panasonic_agv, etc...
        const orgId = getOrgIdFromBasename(ROUTE_BASENAME);
        if (orgId) {
            // If there is orgId in the URL, fetch organization info for that orgId
            const orgByOrgId = await RitzAPI.getInstance().getOrganizationById(orgId);
            return {
                orgByBaseUrl,
                orgByOrgId,
            };
        }

        // The URL does not contain orgId therefore we can just return orgByBaseUrl
        return {
            orgByBaseUrl,
        };
    } catch (error) {
        // Bad baseUrl or orgId, return undefined
        return undefined;
    }
};



const App: React.FC = () => {
    const classes = useStyle();

    const [organizationLoaded, setOrganizationLoaded] = useState<boolean>(false);


    // If we have not load organization data yet, load it
    useEffect(() => {
        if (!organizationLoaded) {
            loadOrganization().then((response) => {

                /**
                 * Check to make sure orgByBaseUrl and orgByOrgId are for the same organization if both are provided. Unless orgByBaseUrl is for
                 * DEFAULT OPS Console URL which we will allow orgByOrgId to be different. This is ALLOWED e.g. https://ops.tinkermode.dev/orgs/x_company
                 * But this is NOT allowed https://yco-ops.tinkermode.dev/orgs/x_company
                 */
                if (response && response.orgByBaseUrl && response.orgByOrgId && response.orgByBaseUrl.id !== DEFAULT_ORGANIZATION_ID
                    && response.orgByBaseUrl.id !== response.orgByOrgId.id) {
                    // Organizations don't match
                    organization = undefined;
                } else {
                    // Set the GLOBAL organization to the response.orgByOrgId FIRST if not available then response.orgByBaseUrl
                    // The orgByOrgId takes precedence
                    organization = response?.orgByOrgId ?? response?.orgByBaseUrl;
                }

                // Now check to see if we need to redirect the user.
                if (response && response.orgByOrgId && response.orgByBaseUrl.id === response.orgByOrgId.id) {
                    // This is Case 2 which the orgId in the URL is redundant e.g. https://yco-ops.tinkermode.dev/orgs/y_company or
                    // https://ops.tinkermode.dev/orgs/default
                    // We will remove the orgId from the URL and redirect the user to the URL without the orgId.
                    // E.g. from https://yco-ops.tinkermode.dev/orgs/y_company/projects/123 to https://yco-ops.tinkermode.dev/projects/123
                    window.location.href = window.location.href.replace(`${ROUTE_WITH_ORG_ID_PREFIX}${response.orgByOrgId.id}`, '');
                } else if (organization && organization.opsConsoleSettings?.baseUrl && organization.opsConsoleSettings.baseUrl !== window.origin) {
                    // If the organization info contains "baseUrl" that means it has custom hostname but the current hostname in the URL is
                    // not the same then we will take the user to the URL with custom hostname. This is the case when the user enters the
                    // page at this URL for example: https://ops.tinkermode.dev/orgs/panasonic_agv and "panasonic" has its own custom hostname
                    // e.g. https://panasonic.agv.com. Therefore, we will take the user to "https://panasonic.agv.com" instead.
                    window.location.href = window.location.href.replace(window.location.origin, organization.opsConsoleSettings.baseUrl);
                } else {
                    // Only setOrganizationLoaded if we are not going to redirect the user
                    setOrganizationLoaded(true);
                }
            });
        }
    }, [organizationLoaded]);


    if (!organizationLoaded) {
        return <CircularProgress />;
    }

    return (
        <ModeUIStateProvider>
            <GlobalDataStateProviderContainer>
                <BrowserRouter basename={ROUTE_BASENAME}>
                    <ModeThemeProvider>
                        <TranslationProvider>
                            <DatePickerUtilsProvider>
                                <CssBaseline>
                                    <div className={classes.app}>
                                        <LoadingContainer />
                                        <NotificationProvider />
                                        <GenericDialogProvider />
                                        <AppRoutes />
                                    </div>
                                </CssBaseline>
                            </DatePickerUtilsProvider>
                        </TranslationProvider>
                    </ModeThemeProvider>
                </BrowserRouter>
            </GlobalDataStateProviderContainer>
        </ModeUIStateProvider>
    );
};

export default App;
